import z from 'zod';

import {
    type AllSideSheets,
    type NavigationScreenModifier,
    type NavigationScreenState,
    NavigationScreenStateSchema,
} from '@gitbook/app-navigation';

import type { LocationTo } from './history';

/**
 * Schema for the navigation state.
 */
export const NavigationStateSchema = z
    .object({
        screen: NavigationScreenStateSchema,
        overlay: NavigationScreenStateSchema.optional(),
    })
    .strict();

/**
 * Global navigation state in the application.
 */
export interface NavigationState {
    screen: NavigationScreenState;
    overlay?: NavigationScreenState;
}

export type NavigationTarget =
    | {
          // Navigate in main screen (close any overlay)
          screen: NavigationScreenModifier;
      }
    | {
          // Open an overlay
          overlay: NavigationScreenModifier;
      }
    | {
          // Update the background screen, preserving the overlay if any
          background: NavigationScreenModifier;
      }
    | {
          // Open an overlay on top of a screen
          screen: NavigationScreenModifier;
          overlay?: NavigationScreenModifier;
      }
    | {
          // Close the overlay. If no overlay, then open "screen"
          overlay: null;
          screen?: NavigationScreenModifier;
      }
    | {
          // Return to the previous non-settings screen in history (settings
          // "Back to app"). Falls back to `screen` when there is no such entry —
          // e.g. settings opened via a direct link or after a reload.
          back: 'app';
          screen: NavigationScreenModifier;
      }
    | {
          // Navigate in the current screen (overlay or main)
          current: NavigationScreenModifier;
      };

/**
 * Function to get an history descriptor for a target.
 */
export type NavigateGetLocationFunction = (to: NavigationTarget) => LocationTo;

/**
 * Function to get the target pathname of a navigation.
 */
export type NavigateURLFunction = (to: NavigationTarget) => string;

/**
 * Function to resolve the side sheet that was open before the one in the given navigation state,
 * on the same subject. Null when there is none, which is how a panel decides not to offer a way
 * back. The side sheet itself rather than its type, so a panel with tabs is restored as it was.
 */
export type NavigateBackSideSheetFunction = (state: NavigationState) => AllSideSheets | null;

/**
 * Navigation options.
 */
export type NavigateOptions = {
    /**
     * Instead of pushing an entry in history, it replaces the last one
     */
    replace?: boolean;
    /**
     * Use a suspense transition to defer rendering till data are loaded.
     * Useful when navigating after creating data that can cause next rendering to be suspended
     */
    suspenseTransition?: boolean;
};

/**
 * Function to navigate in the application.
 */
export type NavigateFunction = (to: NavigationTarget, options?: NavigateOptions) => void;

/**
 * Parse a navigation state.
 */
export function parseNavigationState(input: unknown): NavigationState | null {
    const result = NavigationStateSchema.safeParse(input);
    if (!result.success) {
        return null;
    }
    return result.data;
}
